<template>
  <div class="tournament-landing" :style="themeStyles">
    <!-- Header with Language Selector -->
    <header class="landing-header" :class="{ 'scrolled': isScrolled }">
      <v-container>
        <div class="header-content">
          <div class="header-logo">
            <img v-if="tournament.organizerLogo" :src="tournament.organizerLogo" alt="Logo" class="header-logo-img">
          </div>
          
          <!-- Navigation Menu -->
          <nav class="header-nav d-none d-md-flex">
            <a href="#home" @click.prevent="scrollToSection('home')" :class="{ active: activeSection === 'home' }">Home</a>
            <a href="#prizes" @click.prevent="scrollToSection('prizes')" :class="{ active: activeSection === 'prizes' }">Prizes</a>
            <a href="#how-it-works" @click.prevent="scrollToSection('how-it-works')" :class="{ active: activeSection === 'how-it-works' }">How It Works</a>
            <a href="#rules" @click.prevent="scrollToSection('rules')" :class="{ active: activeSection === 'rules' }">Rules</a>
            <a href="#registration" @click.prevent="scrollToSection('registration')" :class="{ active: activeSection === 'registration' }">Register</a>
          </nav>

          <!-- Mobile Menu Toggle -->
          <v-btn icon class="d-md-none mobile-menu-toggle" @click="mobileMenuOpen = !mobileMenuOpen">
            <v-icon>{{ mobileMenuOpen ? 'mdi-close' : 'mdi-menu' }}</v-icon>
          </v-btn>
          
          <div class="header-actions">
            <!-- Language Selector with Flags -->
            <v-menu offset-y :close-on-content-click="true">
              <template v-slot:activator="{ on, attrs }">
                <div class="language-selector" v-bind="attrs" v-on="on">
                  <img :src="getCurrentFlag()" :alt="currentLanguage" class="flag-icon">
                  <span class="lang-code">{{ currentLanguage.toUpperCase() }}</span>
                  <v-icon small>mdi-chevron-down</v-icon>
                </div>
              </template>
              <v-list class="language-menu">
                <v-list-item 
                  v-for="lang in availableLanguages" 
                  :key="lang.code" 
                  @click="changeLanguage(lang.code)"
                  :class="{ 'active-language': lang.code === currentLanguage }"
                >
                  <v-list-item-avatar tile size="28" height="20">
                    <img :src="lang.flag" :alt="lang.code">
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>
                      <span class="lang-name">{{ lang.name }}</span>
                      <span class="lang-code-menu">{{ lang.code.toUpperCase() }}</span>
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
            
            <!-- Register Button -->
            <v-btn 
              :color="tournament.primaryColor" 
              class="white--text header-register-btn d-none d-sm-flex"
              @click="scrollToRegistration"
            >
              Register Now
            </v-btn>
          </div>
        </div>
        
        <!-- Mobile Navigation Menu -->
        <v-expand-transition>
          <div v-show="mobileMenuOpen" class="mobile-nav d-md-none">
            <a href="#home" @click.prevent="scrollToSection('home'); mobileMenuOpen = false">Home</a>
            <a href="#prizes" @click.prevent="scrollToSection('prizes'); mobileMenuOpen = false">Prizes</a>
            <a href="#how-it-works" @click.prevent="scrollToSection('how-it-works'); mobileMenuOpen = false">How It Works</a>
            <a href="#rules" @click.prevent="scrollToSection('rules'); mobileMenuOpen = false">Rules</a>
            <a href="#registration" @click.prevent="scrollToSection('registration'); mobileMenuOpen = false">Register</a>
          </div>
        </v-expand-transition>
      </v-container>
    </header>

    <!-- Hero Section with Banner -->
    <section class="hero-section" id="home" :style="heroBackgroundStyle">
      <div class="hero-overlay" :style="overlayStyle">
        <v-container>
          <!-- Organizer Logo -->
          <div class="organizer-branding">
            <img v-if="tournament.organizerLogo" :src="tournament.organizerLogo" alt="Organizer Logo" class="organizer-logo">
          </div>

          <!-- Tournament Title & Info -->
          <div class="hero-content" :style="{ color: heroTextColor }">
            <div class="tournament-badge" v-if="tournamentStatus">
              <span :class="['status-badge', tournamentStatus.toLowerCase().replace(' ', '-')]">
                <i class="fas fa-circle pulse"></i>
                {{ tournamentStatus }}
              </span>
            </div>
            
            <h1 class="tournament-title animate-fade-in">{{ tournament.name }}</h1>
            <p class="tournament-subtitle animate-fade-in-delay">{{ tournament.subtitle }}</p>
            
            <!-- Tournament Timer -->
            <div class="timer-container animate-scale-in">
              <div class="timer-label">{{ timerLabel }}</div>
              <div class="timer-display">
                <div class="timer-unit">
                  <div class="timer-value">{{ timer.days }}</div>
                  <div class="timer-unit-label">Days</div>
                </div>
                <div class="timer-separator">:</div>
                <div class="timer-unit">
                  <div class="timer-value">{{ timer.hours }}</div>
                  <div class="timer-unit-label">Hours</div>
                </div>
                <div class="timer-separator">:</div>
                <div class="timer-unit">
                  <div class="timer-value">{{ timer.minutes }}</div>
                  <div class="timer-unit-label">Minutes</div>
                </div>
                <div class="timer-separator">:</div>
                <div class="timer-unit">
                  <div class="timer-value">{{ timer.seconds }}</div>
                  <div class="timer-unit-label">Seconds</div>
                </div>
              </div>
            </div>

            <!-- CTA Buttons -->
            <div class="hero-cta-buttons animate-fade-in-delay-2">
              <v-btn 
                x-large 
                :color="tournament.primaryColor || 'primary'" 
                class="register-btn elevation-8"
                @click="scrollToRegistration"
              >
                <i class="fas fa-trophy mr-2"></i>
                Register Now
              </v-btn>
              <v-btn 
                x-large 
                outlined 
                :color="heroTextColor === 'black' ? 'black' : 'white'" 
                class="learn-more-btn ml-4"
                @click="scrollToInfo"
              >
                Learn More
                <i class="fas fa-arrow-down ml-2"></i>
              </v-btn>
            </div>
          </div>
        </v-container>
      </div>
    </section>

    <!-- Key Highlights Bar -->
    <section class="highlights-bar">
      <v-container>
        <v-row>
          <v-col cols="12" md="3" sm="6" class="highlight-item">
            <div class="highlight-icon">
              <i class="fas fa-dollar-sign"></i>
            </div>
            <div class="highlight-content">
              <div class="highlight-value">{{ tournament.totalPrize }}</div>
              <div class="highlight-label">Total Prize Pool</div>
            </div>
          </v-col>
          <v-col cols="12" md="3" sm="6" class="highlight-item">
            <div class="highlight-icon">
              <i class="fas fa-users"></i>
            </div>
            <div class="highlight-content">
              <div class="highlight-value">{{ tournament.participants }}</div>
              <div class="highlight-label">Participants</div>
            </div>
          </v-col>
          <v-col cols="12" md="3" sm="6" class="highlight-item">
            <div class="highlight-icon">
              <i class="fas fa-calendar"></i>
            </div>
            <div class="highlight-content">
              <div class="highlight-value">{{ tournament.duration }}</div>
              <div class="highlight-label">Duration</div>
            </div>
          </v-col>
          <v-col cols="12" md="3" sm="6" class="highlight-item">
            <div class="highlight-icon">
              <i class="fas fa-chart-line"></i>
            </div>
            <div class="highlight-content">
              <div class="highlight-value">{{ tournament.accountType }}</div>
              <div class="highlight-label">Account Type</div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Prize Pool Section -->
    <section class="prizes-section" id="prizes">
      <v-container>
        <div class="section-header">
          <h2 class="section-title">Prize Distribution</h2>
          <p class="section-subtitle">Compete for amazing rewards</p>
        </div>
        
        <v-row>
          <!-- Podium Display -->
          <v-col cols="12" lg="6">
            <div class="podium-container">
              <!-- 2nd Place -->
              <div class="podium-place second-place">
                <div class="podium-medal">
                  <i class="fas fa-medal"></i>
                </div>
                <div class="podium-rank">2nd</div>
                <div class="podium-prize">{{ tournament.prizes[1] }}</div>
                <div class="podium-bar" :style="{background: tournament.secondaryColor}"></div>
              </div>
              
              <!-- 1st Place -->
              <div class="podium-place first-place">
                <div class="podium-medal gold">
                  <i class="fas fa-trophy"></i>
                </div>
                <div class="podium-rank">1st</div>
                <div class="podium-prize">{{ tournament.prizes[0] }}</div>
                <div class="podium-bar" :style="{background: tournament.primaryColor}"></div>
              </div>
              
              <!-- 3rd Place -->
              <div class="podium-place third-place">
                <div class="podium-medal">
                  <i class="fas fa-medal"></i>
                </div>
                <div class="podium-rank">3rd</div>
                <div class="podium-prize">{{ tournament.prizes[2] }}</div>
                <div class="podium-bar" :style="{background: tournament.tertiaryColor}"></div>
              </div>
            </div>
          </v-col>

          <!-- Prize List -->
          <v-col cols="12" lg="6">
            <div class="prize-list">
              <h3 class="prize-list-title">All Prizes</h3>
              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>Position</th>
                      <th>Prize</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(prize, index) in tournament.allPrizes" :key="index">
                      <td>{{ prize.position }}</td>
                      <td class="prize-amount">{{ prize.amount }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- How It Works Section -->
    <section class="steps-section" id="how-it-works">
      <v-container>
        <div class="section-header">
          <h2 class="section-title">How to Participate</h2>
          <p class="section-subtitle">Join the competition in 4 easy steps</p>
        </div>

        <v-row>
          <v-col v-for="(step, index) in steps" :key="index" cols="12" md="3" sm="6">
            <div class="step-card" :class="{ 'active': hoveredStep === index }" @mouseenter="hoveredStep = index" @mouseleave="hoveredStep = null">
              <div class="step-number" :style="{background: tournament.primaryColor}">{{ index + 1 }}</div>
              <div class="step-icon">
                <i :class="step.icon"></i>
              </div>
              <h3 class="step-title">{{ step.title }}</h3>
              <p class="step-description">{{ step.description }}</p>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Rules Section -->
    <section class="rules-section" id="rules">
      <v-container>
        <v-row>
          <v-col cols="12" md="6">
            <div class="rules-content">
              <h2 class="section-title">Tournament Rules</h2>
              <div class="rules-list">
                <div v-for="(rule, index) in tournament.rules" :key="index" class="rule-item">
                  <div class="rule-icon">
                    <i class="fas fa-check-circle" :style="{color: tournament.primaryColor}"></i>
                  </div>
                  <div class="rule-text">
                    <strong>{{ rule.title }}:</strong> {{ rule.description }}
                  </div>
                </div>
              </div>
            </div>
          </v-col>
          
          <v-col cols="12" md="6">
            <div class="important-dates">
              <h3 class="dates-title">Important Dates</h3>
              <v-timeline dense>
                <v-timeline-item
                  v-for="(date, index) in tournament.importantDates"
                  :key="index"
                  :color="tournament.primaryColor"
                  small
                >
                  <template v-slot:opposite>
                    <span class="date-label">{{ date.date }}</span>
                  </template>
                  <div class="date-event">
                    <strong>{{ date.event }}</strong>
                    <p>{{ date.description }}</p>
                  </div>
                </v-timeline-item>
              </v-timeline>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Registration CTA Section -->
    <section class="registration-section" id="registration" :style="{background: `linear-gradient(135deg, ${tournament.primaryColor}15, ${tournament.primaryColor}05)`}">
      <v-container>
        <div class="registration-card elevation-12">
          <v-row align="center">
            <v-col cols="12" md="8">
              <h2 class="registration-title">Ready to Compete?</h2>
              <p class="registration-subtitle">Join {{ tournament.participants }}+ traders in this exciting competition</p>
              <ul class="registration-benefits">
                <li><i class="fas fa-check mr-2"></i> Real-time leaderboard tracking</li>
                <li><i class="fas fa-check mr-2"></i> Professional trading environment</li>
                <li><i class="fas fa-check mr-2"></i> Amazing prizes and recognition</li>
              </ul>
            </v-col>
            <v-col cols="12" md="4" class="text-center">
              <div class="registration-cta">
                <v-btn 
                  x-large 
                  :color="tournament.primaryColor" 
                  class="white--text elevation-8 register-main-btn"
                  @click="openRegistration"
                >
                  Register Now
                  <i class="fas fa-arrow-right ml-2"></i>
                </v-btn>
                <p class="registration-note mt-3">Limited spots available</p>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-container>
    </section>

    <!-- Sponsor Section -->
    <section class="sponsors-section" v-if="tournament.sponsors && tournament.sponsors.length">
      <v-container>
        <div class="section-header">
          <h2 class="section-title">Our Partners</h2>
        </div>
        <div class="sponsors-grid">
          <div v-for="(sponsor, index) in tournament.sponsors" :key="index" class="sponsor-item">
            <img :src="sponsor.logo" :alt="sponsor.name" class="sponsor-logo">
          </div>
        </div>
      </v-container>
    </section>

    <!-- Footer -->
    <footer class="landing-footer" :style="footerStyle">
      <v-container>
        <div class="footer-content">
          <div class="footer-branding">
            <img v-if="tournament.organizerLogo" :src="tournament.organizerLogo" alt="Organizer" class="footer-logo">
            <p class="footer-text" :style="{ color: footerTextColor }">Organized by {{ tournament.organizer }}</p>
          </div>
          <div class="footer-links">
            <a href="#" @click.prevent="openTerms" :style="{ color: footerLinkColor }">Terms & Conditions</a>
            <a href="#" @click.prevent="openPrivacy" :style="{ color: footerLinkColor }">Privacy Policy</a>
            <a href="#" @click.prevent="openContact" :style="{ color: footerLinkColor }">Contact Us</a>
          </div>
          <div class="footer-powered">
            <p :style="{ color: footerTextColor }">
              Powered by 
              <a href="https://swiset.com" target="_blank" :style="{ color: footerAccentColor }">Swiset</a>
            </p>
          </div>
        </div>
      </v-container>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'TournamentLanding',
  data() {
    return {
      hoveredStep: null,
      timer: {
        days: '00',
        hours: '00',
        minutes: '00',
        seconds: '00'
      },
      timerInterval: null,
      heroTextColor: 'white', // Default to white
      isScrolled: false,
      activeSection: 'home',
      mobileMenuOpen: false,
      currentLanguage: 'en',
      availableLanguages: [
        { 
          code: 'en', 
          name: 'English',
          flag: 'https://flagcdn.com/w40/us.png'
        },
        { 
          code: 'es', 
          name: 'Español',
          flag: 'https://flagcdn.com/w40/es.png'
        },
        { 
          code: 'pt', 
          name: 'Português',
          flag: 'https://flagcdn.com/w40/br.png'
        },
        { 
          code: 'zh', 
          name: '中文',
          flag: 'https://flagcdn.com/w40/cn.png'
        },
        { 
          code: 'ja', 
          name: '日本語',
          flag: 'https://flagcdn.com/w40/jp.png'
        },
        { 
          code: 'ko', 
          name: '한국어',
          flag: 'https://flagcdn.com/w40/kr.png'
        }
      ],
      // Demo tournament data - this would come from props/API in production
      tournament: {
        name: "Global Trading Championship 2024",
        subtitle: "Compete with the best traders worldwide and win amazing prizes",
        organizer: "Premium Broker Ltd.",
        organizerLogo: "https://via.placeholder.com/200x80/333/fff?text=BROKER+LOGO",
        bannerImage: "https://via.placeholder.com/1920x600/000000/333333?text=Dark+Banner+Example", // Dark banner for testing
        bannerDominantColor: null, // Set to null for auto-detection, or specify hex like "000000" for black
        overlayOpacity: 0.5, // Adjustable overlay opacity
        primaryColor: "#4CAF50",
        secondaryColor: "#2196F3", 
        tertiaryColor: "#FF9800",
        footerBackground: "#f8f9fa", // Light footer by default (or use "#1a1a1a" for dark)
        startDate: new Date('2024-02-01'),
        endDate: new Date('2024-02-29'),
        totalPrize: "$100,000",
        participants: "500+",
        duration: "30 Days",
        accountType: "Demo Account",
        prizes: ["$50,000", "$30,000", "$20,000"],
        allPrizes: [
          { position: "1st Place", amount: "$50,000" },
          { position: "2nd Place", amount: "$30,000" },
          { position: "3rd Place", amount: "$20,000" },
          { position: "4th-10th", amount: "$2,000 each" },
          { position: "11th-20th", amount: "$500 each" },
          { position: "21st-50th", amount: "$100 each" }
        ],
        rules: [
          {
            title: "Minimum Deposit",
            description: "$1,000 starting balance for all participants"
          },
          {
            title: "Trading Period",
            description: "30 days of active trading from start date"
          },
          {
            title: "Allowed Instruments",
            description: "All Forex pairs, Indices, and Commodities"
          },
          {
            title: "Maximum Leverage",
            description: "1:100 leverage limit for all trades"
          },
          {
            title: "Risk Management",
            description: "Maximum 10% drawdown allowed"
          }
        ],
        importantDates: [
          {
            date: "Jan 15, 2024",
            event: "Registration Opens",
            description: "Start your journey"
          },
          {
            date: "Feb 1, 2024",
            event: "Competition Starts",
            description: "Trading period begins"
          },
          {
            date: "Feb 29, 2024",
            event: "Competition Ends",
            description: "Final day of trading"
          },
          {
            date: "Mar 5, 2024",
            event: "Winners Announced",
            description: "Prize distribution"
          }
        ],
        sponsors: [
          { name: "Sponsor 1", logo: "https://via.placeholder.com/150x60/f0f0f0/999?text=Partner+1" },
          { name: "Sponsor 2", logo: "https://via.placeholder.com/150x60/f0f0f0/999?text=Partner+2" },
          { name: "Sponsor 3", logo: "https://via.placeholder.com/150x60/f0f0f0/999?text=Partner+3" },
          { name: "Sponsor 4", logo: "https://via.placeholder.com/150x60/f0f0f0/999?text=Partner+4" }
        ]
      },
      steps: [
        {
          icon: "fas fa-user-plus",
          title: "Register",
          description: "Create your account and complete the registration form"
        },
        {
          icon: "fas fa-wallet",
          title: "Fund Account",
          description: "Deposit the minimum required amount to participate"
        },
        {
          icon: "fas fa-chart-line",
          title: "Start Trading",
          description: "Trade during the competition period and climb the leaderboard"
        },
        {
          icon: "fas fa-trophy",
          title: "Win Prizes",
          description: "Top performers win cash prizes and recognition"
        }
      ]
    }
  },
  computed: {
    tournamentStatus() {
      const now = new Date();
      if (now < this.tournament.startDate) return 'Registration open';
      if (now >= this.tournament.startDate && now <= this.tournament.endDate) return 'In progress';
      return 'Ended';
    },
    timerLabel() {
      const now = new Date();
      if (now < this.tournament.startDate) return 'Tournament starts in';
      if (now >= this.tournament.startDate && now <= this.tournament.endDate) return 'Tournament ends in';
      return 'Tournament ended';
    },
    heroBackgroundStyle() {
      return {
        backgroundImage: `url(${this.tournament.bannerImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      };
    },
    themeStyles() {
      return {
        '--primary-color': this.tournament.primaryColor,
        '--secondary-color': this.tournament.secondaryColor,
        '--tertiary-color': this.tournament.tertiaryColor
      };
    },
    overlayStyle() {
      // Adjust overlay based on whether we need light or dark text
      const opacity = this.tournament.overlayOpacity || 0.5;
      
      if (this.heroTextColor === 'black') {
        // Light overlay for dark text
        return {
          background: `linear-gradient(135deg, rgba(255,255,255,${opacity}), rgba(255,255,255,${opacity * 0.7}))`
        };
      } else {
        // Dark overlay for light text (default)
        return {
          background: `linear-gradient(135deg, rgba(0,0,0,${opacity}), rgba(0,0,0,${opacity * 0.7}))`
        };
      }
    },
    footerStyle() {
      const bgColor = this.tournament.footerBackground || '#f8f9fa';
      return {
        background: bgColor,
        borderTop: '1px solid ' + (this.isLightBackground(bgColor) ? '#e0e0e0' : '#333')
      };
    },
    footerTextColor() {
      const bgColor = this.tournament.footerBackground || '#f8f9fa';
      return this.getContrastYIQ(bgColor.replace('#', '')) === 'black' ? '#666' : '#999';
    },
    footerLinkColor() {
      const bgColor = this.tournament.footerBackground || '#f8f9fa';
      return this.getContrastYIQ(bgColor.replace('#', '')) === 'black' ? '#333' : '#ccc';
    },
    footerAccentColor() {
      const bgColor = this.tournament.footerBackground || '#f8f9fa';
      const isLight = this.isLightBackground(bgColor);
      // Use primary color if it contrasts well, otherwise use a safe color
      if (isLight) {
        return this.tournament.primaryColor || '#4CAF50';
      } else {
        // For dark backgrounds, use a lighter version of primary or fallback
        return this.lightenColor(this.tournament.primaryColor) || '#66BB6A';
      }
    }
  },
  mounted() {
    this.startTimer();
    this.loadConfiguration();
    this.detectHeroTextColor();
    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('message', this.handleMessage);
    this.handleScroll(); // Check initial scroll position
  },
  beforeDestroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('message', this.handleMessage);
  },
  methods: {
    // Helper method to determine if text should be black or white based on background
    getContrastYIQ(hexcolor) {
      if (!hexcolor) return 'white';
      
      // Remove hash if it exists
      hexcolor = hexcolor.replace('#', '');
      
      const r = parseInt(hexcolor.substr(0, 2), 16);
      const g = parseInt(hexcolor.substr(2, 2), 16);
      const b = parseInt(hexcolor.substr(4, 2), 16);
      
      const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
      
      return (yiq >= 128) ? 'black' : 'white';
    },
    detectHeroTextColor() {
      // For now, we'll use a simple approach based on the overlay
      // In production, you might want to analyze the actual banner image
      // The overlay darkens the image, so white text usually works
      // But if you have a light banner, you might want to adjust this
      
      // If there's a dominant color specified, use that for contrast
      if (this.tournament.bannerDominantColor) {
        this.heroTextColor = this.getContrastYIQ(this.tournament.bannerDominantColor);
      } else {
        // Default to white since we have a dark overlay
        this.heroTextColor = 'white';
      }
    },
    startTimer() {
      this.updateTimer();
      this.timerInterval = setInterval(this.updateTimer, 1000);
    },
    updateTimer() {
      const now = new Date();
      let targetDate = this.tournament.startDate;
      
      if (now >= this.tournament.startDate) {
        targetDate = this.tournament.endDate;
      }
      
      const diff = targetDate - now;
      
      if (diff <= 0) {
        this.timer = {
          days: '00',
          hours: '00',
          minutes: '00',
          seconds: '00'
        };
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      this.timer = {
        days: String(days).padStart(2, '0'),
        hours: String(hours).padStart(2, '0'),
        minutes: String(minutes).padStart(2, '0'),
        seconds: String(seconds).padStart(2, '0')
      };
    },
    scrollToRegistration() {
      this.scrollToSection('registration');
    },
    scrollToInfo() {
      this.scrollToSection('prizes');
    },
    scrollToSection(sectionId) {
      const element = document.getElementById(sectionId);
      if (element) {
        const headerHeight = 80; // Account for sticky header
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - headerHeight;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
        
        this.activeSection = sectionId;
        this.mobileMenuOpen = false; // Close mobile menu if open
      }
    },
    handleScroll() {
      // Update header background on scroll
      this.isScrolled = window.scrollY > 50;
      
      // Update active section based on scroll position
      const sections = ['home', 'prizes', 'how-it-works', 'rules', 'registration'];
      const scrollPosition = window.scrollY + 100;
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { top, bottom } = element.getBoundingClientRect();
          const elementTop = top + window.scrollY;
          const elementBottom = bottom + window.scrollY;
          
          if (scrollPosition >= elementTop && scrollPosition < elementBottom) {
            this.activeSection = section;
            break;
          }
        }
      }
    },
    changeLanguage(langCode) {
      this.currentLanguage = langCode;
      // In production, this would trigger i18n language change
      // this.$i18n.locale = langCode;
      // eslint-disable-next-line no-console
      console.log(`Language changed to: ${langCode}`);
    },
    getCurrentFlag() {
      const currentLang = this.availableLanguages.find(lang => lang.code === this.currentLanguage);
      return currentLang ? currentLang.flag : this.availableLanguages[0].flag;
    },
    isLightBackground(color) {
      if (!color) return true;
      const hexColor = color.replace('#', '');
      return this.getContrastYIQ(hexColor) === 'black';
    },
    lightenColor(color) {
      if (!color) return '#66BB6A';
      // Convert hex to RGB and lighten by 20%
      const hex = color.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      
      const lighter = (val) => Math.min(255, Math.floor(val + (255 - val) * 0.3));
      
      const newR = lighter(r).toString(16).padStart(2, '0');
      const newG = lighter(g).toString(16).padStart(2, '0');
      const newB = lighter(b).toString(16).padStart(2, '0');
      
      return `#${newR}${newG}${newB}`;
    },
    openRegistration() {
      // This would open the registration modal or redirect to registration page
      // eslint-disable-next-line no-console
      console.log('Opening registration...');
      // In production, this would:
      // this.$router.push({ name: 'tournament-embed-register', query: { tournamentId: this.tournament.id } })
    },
    openTerms() {
      // eslint-disable-next-line no-console
      console.log('Opening terms...');
      // In production, this would open terms modal or redirect
    },
    openPrivacy() {
      // eslint-disable-next-line no-console
      console.log('Opening privacy policy...');
      // In production, this would open privacy policy modal or redirect
    },
    openContact() {
      // eslint-disable-next-line no-console
      console.log('Opening contact...');
      // In production, this would open contact modal or redirect
    },
    loadConfiguration() {
      // Check if configuration is passed via URL params
      const urlParams = new URLSearchParams(window.location.search);
      const configParam = urlParams.get('config');
      
      if (configParam) {
        try {
          const config = JSON.parse(decodeURIComponent(configParam));
          this.applyConfiguration(config);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Failed to parse configuration from URL');
        }
      } else {
        // Check localStorage for saved configuration
        const saved = localStorage.getItem('tournamentLandingConfig');
        if (saved) {
          try {
            const config = JSON.parse(saved);
            this.applyConfiguration(config);
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to load saved configuration');
          }
        }
      }
    },
    applyConfiguration(config) {
      // Apply all configuration values to tournament object
      this.tournament = {
        ...this.tournament,
        name: config.name || this.tournament.name,
        subtitle: config.subtitle || this.tournament.subtitle,
        organizer: config.organizer || this.tournament.organizer,
        organizerLogo: config.organizerLogo || this.tournament.organizerLogo,
        bannerImage: config.bannerImage || this.tournament.bannerImage,
        primaryColor: config.primaryColor || this.tournament.primaryColor,
        secondaryColor: config.secondaryColor || this.tournament.secondaryColor,
        tertiaryColor: config.tertiaryColor || this.tournament.tertiaryColor,
        footerBackground: config.footerBackground || this.tournament.footerBackground,
        overlayOpacity: config.overlayOpacity !== undefined ? config.overlayOpacity : this.tournament.overlayOpacity,
        totalPrize: config.totalPrize ? `$${config.totalPrize}` : this.tournament.totalPrize,
        participants: config.participants ? `${config.participants}+` : this.tournament.participants,
        duration: config.duration ? `${config.duration} Days` : this.tournament.duration,
        accountType: config.accountType || this.tournament.accountType,
        prizes: config.prizes ? config.prizes.map(p => `$${p}`) : this.tournament.prizes
      };
      
      // Re-detect text color after applying new configuration
      this.$nextTick(() => {
        this.detectHeroTextColor();
      });
    },
    handleMessage(event) {
      // Handle messages from parent window (admin panel)
      if (event.data && event.data.type === 'UPDATE_TOURNAMENT') {
        this.applyConfiguration(event.data.data);
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.tournament-landing {
  font-family: 'Avenir', 'Helvetica Neue', sans-serif;
  padding-top: 70px; // Account for fixed header
  
  /* Header Styles */
  .landing-header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    z-index: 1000;
    transition: all 0.3s ease;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    
    &.scrolled {
      background: rgba(255, 255, 255, 0.98);
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    }
    
    .header-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 70px;
      
      .header-logo {
        .header-logo-img {
          height: 40px;
          max-width: 150px;
          object-fit: contain;
        }
      }
      
      .header-nav {
        display: flex;
        align-items: center;
        gap: 30px;
        flex: 1;
        justify-content: center;
        
        a {
          color: #333;
          text-decoration: none;
          font-weight: 500;
          font-size: 15px;
          padding: 8px 12px;
          border-radius: 8px;
          transition: all 0.3s ease;
          position: relative;
          
          &:hover {
            color: var(--primary-color);
            background: rgba(76, 175, 80, 0.08);
          }
          
          &.active {
            color: var(--primary-color);
            
            &::after {
              content: '';
              position: absolute;
              bottom: -2px;
              left: 12px;
              right: 12px;
              height: 2px;
              background: var(--primary-color);
              border-radius: 2px;
            }
          }
        }
      }
      
      .mobile-menu-toggle {
        margin-left: auto;
        margin-right: 10px;
      }
      
      .header-actions {
        display: flex;
        align-items: center;
        gap: 15px;
        
        .language-selector {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          border: 1px solid #e0e0e0;
          background: white;
          
          &:hover {
            background: #f5f5f5;
            border-color: #ccc;
          }
          
          .flag-icon {
            width: 20px;
            height: 15px;
            object-fit: cover;
            border-radius: 2px;
            box-shadow: 0 1px 2px rgba(0,0,0,0.1);
          }
          
          .lang-code {
            font-weight: 600;
            font-size: 13px;
            color: #333;
            text-transform: uppercase;
          }
        }
        
        .header-register-btn {
          padding: 8px 20px;
          height: 40px;
          border-radius: 20px;
          text-transform: none;
          font-weight: 600;
          letter-spacing: 0.5px;
        }
      }
    }
    
    .mobile-nav {
      padding: 20px 0;
      border-top: 1px solid #e0e0e0;
      
      a {
        display: block;
        padding: 12px 0;
        color: #333;
        text-decoration: none;
        font-weight: 500;
        transition: all 0.3s ease;
        
        &:hover {
          color: var(--primary-color);
          padding-left: 10px;
        }
      }
    }
  }
  
  /* Language Menu Dropdown Styles */
  .language-menu {
    .v-list-item {
      min-height: 45px;
      transition: all 0.2s ease;
      
      &:hover {
        background: #f5f5f5;
      }
      
      &.active-language {
        background: rgba(76, 175, 80, 0.08);
        
        .lang-name {
          color: var(--primary-color);
          font-weight: 600;
        }
      }
      
      .v-list-item__avatar {
        margin-right: 12px !important;
        min-width: 28px !important;
        height: auto !important;
        
        img {
          width: 28px;
          height: 20px;
          object-fit: cover;
          border-radius: 3px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.15);
          display: block;
        }
      }
      
      .v-list-item__content {
        .v-list-item__title {
          display: flex;
          align-items: center;
          justify-content: space-between;
          
          .lang-name {
            font-size: 14px;
            color: #333;
          }
          
          .lang-code-menu {
            font-size: 12px;
            color: #999;
            font-weight: 500;
            margin-left: auto;
          }
        }
      }
    }
  }
  
  /* Hero Section */
  .hero-section {
    min-height: 100vh;
    position: relative;
    display: flex;
    align-items: center;
    
    .hero-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, rgba(0,0,0,0.7), rgba(0,0,0,0.5));
      display: flex;
      align-items: center;
    }
    
    .organizer-branding {
      text-align: center;
      margin-bottom: 2rem;
      
      .organizer-logo {
        max-height: 80px;
        max-width: 250px;
        object-fit: contain;
      }
    }
    
    .hero-content {
      color: white;
      text-align: center;
      position: relative;
      z-index: 1;
      
      .tournament-badge {
        margin-bottom: 1.5rem;
        
        .status-badge {
          display: inline-flex;
          align-items: center;
          padding: 8px 20px;
          border-radius: 50px;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          font-size: 14px;
          font-weight: 500;
          letter-spacing: 0.5px;
          
          i {
            margin-right: 8px;
            font-size: 8px;
          }
          
          &.registration-open {
            background: rgba(76, 175, 80, 0.3);
            border-color: #4CAF50;
          }
          
          &.in-progress {
            background: rgba(255, 152, 0, 0.3);
            border-color: #FF9800;
          }
          
          &.ended {
            background: rgba(200, 200, 200, 0.3);
            border-color: #999;
          }
        }
      }
      
      .tournament-title {
        font-size: 3.5rem;
        font-weight: bold;
        margin-bottom: 1rem;
        text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
        
        @media (max-width: 768px) {
          font-size: 2.5rem;
        }
      }
      
      .tournament-subtitle {
        font-size: 1.3rem;
        margin-bottom: 3rem;
        opacity: 0.95;
        max-width: 600px;
        margin-left: auto;
        margin-right: auto;
      }
    }
  }
  
  /* Timer Container */
  .timer-container {
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    padding: 2rem;
    margin: 2rem auto;
    max-width: 600px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    
    .timer-label {
      font-size: 1.1rem;
      margin-bottom: 1rem;
      letter-spacing: 1px;
      opacity: 0.9;
    }
    
    .timer-display {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 10px;
      
      .timer-unit {
        text-align: center;
        
        .timer-value {
          font-size: 3rem;
          font-weight: bold;
          line-height: 1;
        }
        
        .timer-unit-label {
          font-size: 0.9rem;
          opacity: 0.8;
          margin-top: 5px;
        }
      }
      
      .timer-separator {
        font-size: 2rem;
        opacity: 0.5;
        margin: 0 5px;
      }
    }
  }
  
  /* CTA Buttons */
  .hero-cta-buttons {
    margin-top: 2rem;
    
    .register-btn {
      font-size: 1.2rem;
      padding: 28px 50px;
      border-radius: 50px;
      text-transform: none;
      font-weight: 600;
    }
    
    .learn-more-btn {
      font-size: 1.1rem;
      padding: 26px 40px;
      border-radius: 50px;
      border: 2px solid;
      text-transform: none;
      font-weight: 500;
    }
  }
  
  /* Highlights Bar */
  .highlights-bar {
    background: #f8f9fa;
    padding: 60px 0;
    border-bottom: 1px solid #e0e0e0;
    
    .highlight-item {
      display: flex;
      align-items: center;
      justify-content: center;
      
      .highlight-icon {
        font-size: 2.5rem;
        color: var(--primary-color);
        margin-right: 1.5rem;
        opacity: 0.8;
      }
      
      .highlight-content {
        text-align: left;
        
        .highlight-value {
          font-size: 2rem;
          font-weight: bold;
          color: #333;
          line-height: 1;
        }
        
        .highlight-label {
          font-size: 0.9rem;
          color: #666;
          margin-top: 5px;
        }
      }
    }
  }
  
  /* Prizes Section */
  .prizes-section {
    padding: 100px 0;
    background: white;
    
    .podium-container {
      display: flex;
      align-items: flex-end;
      justify-content: center;
      height: 400px;
      
      .podium-place {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        
        .podium-medal {
          font-size: 3rem;
          margin-bottom: 10px;
          color: #888;
          
          &.gold {
            color: #FFD700;
          }
        }
        
        .podium-rank {
          font-size: 1.5rem;
          font-weight: bold;
          margin-bottom: 10px;
        }
        
        .podium-prize {
          font-size: 1.8rem;
          font-weight: bold;
          color: var(--primary-color);
          margin-bottom: 20px;
        }
        
        .podium-bar {
          width: 80%;
          background: linear-gradient(to top, var(--primary-color), var(--primary-color));
          border-radius: 10px 10px 0 0;
        }
        
        &.first-place .podium-bar {
          height: 200px;
        }
        
        &.second-place .podium-bar {
          height: 150px;
          opacity: 0.8;
        }
        
        &.third-place .podium-bar {
          height: 100px;
          opacity: 0.6;
        }
      }
    }
    
    .prize-list {
      background: #f8f9fa;
      padding: 30px;
      border-radius: 15px;
      
      .prize-list-title {
        font-size: 1.5rem;
        margin-bottom: 20px;
        color: #333;
      }
      
      .prize-amount {
        font-weight: bold;
        color: var(--primary-color);
      }
    }
  }
  
  /* Steps Section */
  .steps-section {
    padding: 100px 0;
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    
    .step-card {
      background: white;
      padding: 40px 30px;
      border-radius: 20px;
      text-align: center;
      height: 100%;
      transition: all 0.3s ease;
      position: relative;
      cursor: pointer;
      
      &:hover,
      &.active {
        transform: translateY(-10px);
        box-shadow: 0 20px 40px rgba(0,0,0,0.1);
        
        .step-icon {
          transform: scale(1.1);
          color: var(--primary-color);
        }
      }
      
      .step-number {
        position: absolute;
        top: -20px;
        left: 50%;
        transform: translateX(-50%);
        width: 40px;
        height: 40px;
        background: var(--primary-color);
        color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: 1.2rem;
      }
      
      .step-icon {
        font-size: 3rem;
        color: #999;
        margin-bottom: 20px;
        transition: all 0.3s ease;
      }
      
      .step-title {
        font-size: 1.3rem;
        margin-bottom: 15px;
        color: #333;
      }
      
      .step-description {
        color: #666;
        line-height: 1.6;
      }
    }
  }
  
  /* Rules Section */
  .rules-section {
    padding: 100px 0;
    background: white;
    
    .rules-list {
      margin-top: 30px;
      
      .rule-item {
        display: flex;
        margin-bottom: 20px;
        
        .rule-icon {
          margin-right: 15px;
          font-size: 1.2rem;
          padding-top: 2px;
        }
        
        .rule-text {
          flex: 1;
          line-height: 1.6;
          color: #555;
        }
      }
    }
    
    .important-dates {
      background: #f8f9fa;
      padding: 30px;
      border-radius: 15px;
      
      .dates-title {
        font-size: 1.5rem;
        margin-bottom: 30px;
        color: #333;
      }
      
      .date-label {
        font-weight: bold;
        color: #666;
      }
      
      .date-event {
        padding: 15px;
        background: white;
        border-radius: 10px;
        
        p {
          margin-top: 5px;
          color: #666;
          font-size: 0.9rem;
        }
      }
    }
  }
  
  /* Registration Section */
  .registration-section {
    padding: 100px 0;
    
    .registration-card {
      background: white;
      border-radius: 20px;
      padding: 60px;
      
      .registration-title {
        font-size: 2.5rem;
        margin-bottom: 15px;
        color: #333;
      }
      
      .registration-subtitle {
        font-size: 1.2rem;
        color: #666;
        margin-bottom: 30px;
      }
      
      .registration-benefits {
        list-style: none;
        padding: 0;
        
        li {
          padding: 10px 0;
          font-size: 1.1rem;
          color: #555;
          
          i {
            color: var(--primary-color);
          }
        }
      }
      
      .registration-cta {
        .register-main-btn {
          font-size: 1.3rem;
          padding: 30px 50px;
          border-radius: 50px;
        }
        
        .registration-note {
          color: #666;
          font-style: italic;
        }
      }
    }
  }
  
  /* Sponsors Section */
  .sponsors-section {
    padding: 80px 0;
    background: #f8f9fa;
    
    .sponsors-grid {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 50px;
      flex-wrap: wrap;
      margin-top: 50px;
      
      .sponsor-item {
        .sponsor-logo {
          height: 60px;
          object-fit: contain;
          filter: grayscale(100%);
          opacity: 0.7;
          transition: all 0.3s ease;
          
          &:hover {
            filter: grayscale(0%);
            opacity: 1;
          }
        }
      }
    }
  }
  
  /* Footer */
  .landing-footer {
    padding: 40px 0;
    transition: all 0.3s ease;
    
    .footer-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      
      @media (max-width: 768px) {
        flex-direction: column;
        text-align: center;
        gap: 30px;
      }
      
      .footer-logo {
        height: 40px;
        margin-bottom: 10px;
        opacity: 0.9;
      }
      
      .footer-text {
        margin-top: 10px;
        font-size: 14px;
      }
      
      .footer-links {
        display: flex;
        gap: 30px;
        
        a {
          text-decoration: none;
          transition: all 0.3s ease;
          font-weight: 500;
          
          &:hover {
            opacity: 0.8;
            text-decoration: underline;
          }
        }
      }
      
      .footer-powered {
        font-size: 14px;
        
        a {
          text-decoration: none;
          font-weight: 600;
          transition: opacity 0.3s ease;
          
          &:hover {
            opacity: 0.8;
          }
        }
      }
    }
  }
  
  /* Section Headers */
  .section-header {
    text-align: center;
    margin-bottom: 60px;
    
    .section-title {
      font-size: 2.5rem;
      margin-bottom: 15px;
      color: #333;
    }
    
    .section-subtitle {
      font-size: 1.2rem;
      color: #666;
    }
  }
  
  /* Animations */
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
  
  .pulse {
    animation: pulse 2s infinite;
  }
  
  .animate-fade-in {
    animation: fadeIn 1s ease-out;
  }
  
  .animate-fade-in-delay {
    animation: fadeIn 1s ease-out 0.3s both;
  }
  
  .animate-fade-in-delay-2 {
    animation: fadeIn 1s ease-out 0.6s both;
  }
  
  .animate-scale-in {
    animation: scaleIn 0.5s ease-out 0.3s both;
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes scaleIn {
    from {
      transform: scale(0.9);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }
}
</style>